import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { GeneralSidebarComponent } from '../../../components/general-sidebar/general-sidebar.component';
import { LoggedInHeaderComponent } from '../../../components/logged-in-header/logged-in-header.component';
import { PageHeaderComponent } from '../../../components/page-header/page-header.component';
import { StatsGridComponent } from '../../../components/stats-grid/stats-grid.component';
import { CategoryChartComponent } from '../../../components/category-chart/category-chart.component';
import { CategoryFeeSummaryComponent } from '../../../components/category-fee-summary/category-fee-summary.component';
import { RevenueTrendComponent } from '../../../components/revenue-trend/revenue-trend.component';
import { VendorsByMdaChartComponent } from '../../../components/vendors-by-mda-chart/vendors-by-mda-chart.component';
import { PaymentMethodsChartComponent } from '../../../components/payment-methods-chart/payment-methods-chart.component';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import { MetricsService } from '../../../modules/services/metrics.service';
import { REPORT_STAT_CARDS } from './reports-landing.model';
import { StatCard, VendorCategory } from '../../../models/dashboard.model';
import { Subject, debounceTime, takeUntil } from 'rxjs';

@Component({
  selector: 'app-reports-landing',
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    StatsGridComponent,
    RevenueTrendComponent,
    CategoryChartComponent,
    VendorsByMdaChartComponent,
    PaymentMethodsChartComponent,
    CategoryFeeSummaryComponent,
  ],
  templateUrl: './reports-landing.component.html',
  styleUrl: './reports-landing.component.scss',
})
export class ReportsLandingComponent implements OnInit, OnDestroy {
  statCards: StatCard[] = REPORT_STAT_CARDS.map((c) => {
    if (
      c.title === 'Total Revenue' ||
      c.title === 'Active Vendors' ||
      c.title === 'Pending Approvals'
    ) {
      return { ...c, value: 'N/A', change: '' };
    }
    return c;
  });
  revenueTrend: Array<{ month: string; value: number }> = [];
  revenueTrendEmptyMessage = '';
  categories: VendorCategory[] = [];
  vendorsByMda: Array<{ label: string; count: number }> = [];
  paymentMethods: Array<{ label: string; value: number; color: string }> = [];
  categoryFeeSummary: Array<{
    category: string;
    contractRange: string;
    registrationFee: number;
    vendors: number;
    revenue: number;
  }> = [];

  mdas: Array<{ id: string; name: string }> = [];
  categoryOptions: Array<{ id: string; name: string }> = [];
  paymentChannelOptions: string[] = [];
  selectedMdaId = '';
  selectedCategoryId = '';
  selectedPaymentChannel = '';
  fromDate = '';
  toDate = '';
  dateRangePreset:
    | 'all_time'
    | 'last_7_days'
    | 'last_30_days'
    | 'year_to_date'
    | 'custom' = 'last_7_days';

  private mdasLoaded = false;
  private categoriesLoaded = false;
  private paymentChannelsLoaded = false;
  private analyticsCurrency = 'NGN';
  private readonly reload$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly apiService: ApiServiceService,
    private readonly metricsService: MetricsService,
  ) {}

  ngOnInit(): void {
    this.applyDateRangePreset(this.dateRangePreset);
    this.loadDropdownOptions();
    this.reload$
      .pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe(() => this.loadAnalytics());
    this.loadAnalytics();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reload$.complete();
  }

  private loadDropdownOptions(): void {
    this.apiService.get('/mdas').subscribe({
      next: (res: any) => {
        const data = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.data) ? res.data.data : [];
        this.mdas = data
          .map((item: any) => ({
            id: String(item?.id ?? ''),
            name: String(item?.name ?? ''),
          }))
          .filter((m: any) => m.id && m.name);
        this.mdasLoaded = true;
      },
      error: () => {
        if (!this.mdasLoaded) this.mdas = [];
      },
    });

    this.apiService.get('/categories').subscribe({
      next: (res: any) => {
        const data: Array<any> = Array.isArray(res?.data) ? res.data : [];
        this.categoryOptions = data
          .map((item) => ({
            id: String(item?.id ?? ''),
            name: String(item?.name ?? item?.category_name ?? ''),
          }))
          .filter((c) => c.id && c.name);
        this.categoriesLoaded = true;
      },
      error: () => {
        if (!this.categoriesLoaded) this.categoryOptions = [];
      },
    });

    this.metricsService.getPaymentChannels().subscribe({
      next: (payload) => {
        const channels = Array.isArray(payload?.channels) ? payload.channels : [];
        this.paymentChannelOptions = channels
          .map((c) => String(c ?? '').trim())
          .filter(Boolean);
        this.paymentChannelsLoaded = true;
      },
      error: () => {
        if (!this.paymentChannelsLoaded) this.paymentChannelOptions = [];
      },
    });
  }

  private loadAnalytics(): void {
    const filters = this.buildAnalyticsFilters();
    this.metricsService.getReportsAnalytics(filters).subscribe({
      next: (payload) => {
        const cards: any = payload?.cards ?? {};
        const currency = String(cards?.currency ?? 'NGN').trim() || 'NGN';
        this.analyticsCurrency = currency;

        const totalRevenue = Number(cards?.totalRevenue ?? NaN);
        const revenueChangeValue = Number(cards?.revenueChange?.value ?? NaN);
        const revenueChangeIsPct = Boolean(cards?.revenueChange?.percentage);

        this.setStatCard('Total Revenue', {
          value: Number.isFinite(totalRevenue) ? this.formatMoney(totalRevenue, currency) : 'N/A',
          change: this.formatChange(revenueChangeValue, revenueChangeIsPct),
        });

        const activeVendors = Number(cards?.activeVendors ?? NaN);
        const activeVendorsChange = Number(cards?.activeVendorsChange ?? NaN);
        this.setStatCard('Active Vendors', {
          value: Number.isFinite(activeVendors) ? activeVendors.toLocaleString('en-NG') : 'N/A',
          change: this.formatDelta(activeVendorsChange),
        });

        const pendingApprovals = Number(cards?.pendingApprovals ?? NaN);
        this.setStatCard('Pending Approvals', {
          value: Number.isFinite(pendingApprovals) ? pendingApprovals.toLocaleString('en-NG') : 'N/A',
        });

        const avgTime = Number(cards?.avgProcessingTime ?? NaN);
        const avgUnit = String(cards?.avgProcessingTimeUnit ?? '').trim();
        const avgChange = Number(cards?.avgProcessingTimeChange ?? NaN);
        this.setStatCard('Avg. Processing Time', {
          value:
            Number.isFinite(avgTime) && avgUnit
              ? `${avgTime.toLocaleString('en-NG')} ${avgUnit}`
              : Number.isFinite(avgTime)
                ? `${avgTime.toLocaleString('en-NG')}`
                : 'N/A',
          change: this.formatDelta(avgChange, false),
        });

        const palette = ['#16A34A', '#F59E0B', '#1D4ED8', '#0EA5B7', '#8B5CF6', '#EF4444'];
        const categoryDist = Array.isArray(payload?.vendorDistributionByCategory)
          ? payload.vendorDistributionByCategory
          : [];
        this.categories = categoryDist
          .map((item: any, index: number) => ({
            name: String(item?.categoryName ?? '').trim(),
            count: Number(item?.count ?? 0),
            color: palette[index % palette.length],
          }))
          .filter((c) => c.name && c.count > 0);

        const mdas = Array.isArray(payload?.vendorsByMdas) ? payload.vendorsByMdas : [];
        this.vendorsByMda = mdas
          .map((item: any) => ({ label: String(item?.mdaName ?? '').trim(), count: Number(item?.count ?? 0) }))
          .filter((d) => d.label && d.count > 0)
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);

        const feeRows = Array.isArray(payload?.categoryFeeSummary) ? payload.categoryFeeSummary : [];
        this.categoryFeeSummary = feeRows.map((row: any) => ({
          category: String(row?.name ?? ''),
          contractRange: String(row?.contractRange ?? ''),
          registrationFee: Number(row?.registrationFee ?? 0),
          vendors: Number(row?.vendorCount ?? 0),
          revenue: Number(row?.revenue ?? 0),
        }));

        const methods = Array.isArray(payload?.paymentChannels) ? payload.paymentChannels : [];
        const pmPalette = ['#22C55E', '#1D4ED8', '#F59E0B', '#8B5CF6', '#EF4444', '#0EA5B7'];
        this.paymentMethods = methods
          .map((m: any, index: number) => {
            const label = String(m?.channel ?? '').trim();
            const rawValue = Number(m?.percentage ?? 0);
            return {
              label,
              value: Number.isFinite(rawValue) ? rawValue : 0,
              color: pmPalette[index % pmPalette.length],
            };
          })
          .filter((m: any) => m.label);

        const trend = payload?.revenueTrend;
        const granularity = String(payload?.revenueTrendGranularity ?? '').trim().toLowerCase();
        if (!trend) {
          this.revenueTrend = [];
          this.revenueTrendEmptyMessage = 'No payments yet.';
        } else {
          const showYear = this.shouldShowYearInDayLabels();
          this.revenueTrend = trend.map((p: any) => ({
            month: this.formatTrendLabel(String(p?.period ?? ''), granularity, showYear),
            value: Number(p?.value ?? 0),
          }));
          this.revenueTrendEmptyMessage = this.revenueTrend.length ? '' : 'No payments yet.';
        }
      },
      error: () => {
        this.setStatCard('Total Revenue', { value: 'N/A', change: '' });
        this.setStatCard('Active Vendors', { value: 'N/A', change: '' });
        this.setStatCard('Pending Approvals', { value: 'N/A', change: '' });
        this.revenueTrend = [];
        this.revenueTrendEmptyMessage = '';
        this.categories = [];
        this.vendorsByMda = [];
        this.paymentMethods = [];
        this.categoryFeeSummary = [];
      },
    });
  }

  onMdaChange(value: string): void {
    this.selectedMdaId = value;
    this.scheduleReload();
  }

  onCategoryChange(value: string): void {
    this.selectedCategoryId = value;
    this.scheduleReload();
  }

  onPaymentChannelChange(value: string): void {
    this.selectedPaymentChannel = value;
    this.scheduleReload();
  }

  onDateRangePresetChange(value: string): void {
    const preset =
      value === 'all_time' ||
      value === 'last_30_days' ||
      value === 'year_to_date' ||
      value === 'custom'
        ? value
        : 'last_7_days';
    this.dateRangePreset = preset;
    this.applyDateRangePreset(preset);
    this.scheduleReload();
  }

  onFromDateChange(value: string): void {
    this.fromDate = value;
    this.dateRangePreset = 'custom';
    if (this.toDate && this.fromDate && this.toDate < this.fromDate) {
      this.toDate = this.fromDate;
    }
    this.scheduleReload();
  }

  onToDateChange(value: string): void {
    this.toDate = value;
    this.dateRangePreset = 'custom';
    if (this.fromDate && this.toDate && this.toDate < this.fromDate) {
      this.fromDate = this.toDate;
    }
    this.scheduleReload();
  }

  private scheduleReload(): void {
    this.reload$.next();
  }

  private buildAnalyticsFilters(): {
    startDate?: string;
    endDate?: string;
    mdaId?: string;
    categoryId?: string;
    paymentChannel?: string;
  } {
    const filters: any = {};
    if (this.selectedMdaId) filters.mdaId = this.selectedMdaId;
    if (this.selectedCategoryId) filters.categoryId = this.selectedCategoryId;
    if (this.selectedPaymentChannel) filters.paymentChannel = this.selectedPaymentChannel;

    const hasDates = Boolean(this.fromDate) && Boolean(this.toDate) && this.dateRangePreset !== 'all_time';
    if (hasDates) {
      filters.startDate = this.fromDate;
      filters.endDate = this.toDate;
    }
    return filters;
  }

  private applyDateRangePreset(
    preset: 'all_time' | 'last_7_days' | 'last_30_days' | 'year_to_date' | 'custom',
  ): void {
    if (preset === 'custom') return;

    if (preset === 'all_time') {
      this.fromDate = '';
      this.toDate = '';
      return;
    }

    const today = new Date();
    const to = this.formatDateInputValue(today);
    let from = to;

    if (preset === 'last_7_days') {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      from = this.formatDateInputValue(start);
    } else if (preset === 'last_30_days') {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      from = this.formatDateInputValue(start);
    } else if (preset === 'year_to_date') {
      const start = new Date(today.getFullYear(), 0, 1);
      from = this.formatDateInputValue(start);
    }

    this.fromDate = from;
    this.toDate = to;
  }

  private formatDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private setStatCard(title: StatCard['title'], patch: Partial<StatCard>): void {
    const index = this.statCards.findIndex((c) => c.title === title);
    if (index === -1) return;
    this.statCards = this.statCards.map((card, i) => (i === index ? { ...card, ...patch } : card));
  }

  exportExcel(): void {
    const filename = this.buildExportFilename('reports', 'csv');
    const csv = this.buildCsvExport();
    this.downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
  }

  exportPdf(): void {
    const title = 'Reports & Analytics';
    const printableHtml = this.buildPrintableHtml(title);
    const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=1024,height=768');
    if (!printWindow) return;

    printWindow.document.open();
    printWindow.document.write(printableHtml);
    printWindow.document.close();

    // Give the browser a tick to render before printing.
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  }

  private buildCsvExport(): string {
    const lines: string[] = [];

    lines.push(this.csvLine(['Reports Export']));
    lines.push(this.csvLine(['Generated At', new Date().toISOString()]));
    lines.push(this.csvLine(['Date Range', this.getDateRangeLabel()]));
    lines.push(this.csvLine(['MDA', this.getSelectedMdaLabel()]));
    lines.push(this.csvLine(['Category', this.getSelectedCategoryLabel()]));
    lines.push('');

    lines.push(this.csvLine(['Stat Cards']));
    lines.push(this.csvLine(['Title', 'Value', 'Change', 'Subtitle']));
    for (const card of this.statCards) {
      lines.push(this.csvLine([card.title, card.value, card.change ?? '', card.subtitle ?? '']));
    }
    lines.push('');

    lines.push(this.csvLine(['Vendor Distribution by Category']));
    lines.push(this.csvLine(['Category', 'Count']));
    for (const item of this.categories) {
      lines.push(this.csvLine([item.name, String(item.count)]));
    }
    lines.push('');

    lines.push(this.csvLine(['Vendors by MDA']));
    lines.push(this.csvLine(['MDA', 'Count']));
    for (const item of this.vendorsByMda) {
      lines.push(this.csvLine([item.label, String(item.count)]));
    }
    lines.push('');

    lines.push(this.csvLine(['Category Fee Summary']));
    lines.push(this.csvLine(['Category', 'Contract Range', 'Registration Fee', 'Vendors', 'Revenue']));
    for (const row of this.categoryFeeSummary) {
      lines.push(
        this.csvLine([
          String(row.category ?? ''),
          String(row.contractRange ?? ''),
          String(row.registrationFee ?? 0),
          String(row.vendors ?? 0),
          String(row.revenue ?? 0),
        ]),
      );
    }

    return lines.join('\n');
  }

  private buildPrintableHtml(title: string): string {
    const escape = (value: unknown) =>
      String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');

    const statRows = this.statCards
      .map(
        (c) => `
        <tr>
          <td>${escape(c.title)}</td>
          <td style="text-align:right;">${escape(c.value)}</td>
          <td style="text-align:right;">${escape(c.change ?? '')}</td>
          <td>${escape(c.subtitle ?? '')}</td>
        </tr>`,
      )
      .join('');

    const categoryRows = this.categories
      .map(
        (c) => `
        <tr>
          <td>${escape(c.name)}</td>
          <td style="text-align:right;">${escape(c.count)}</td>
        </tr>`,
      )
      .join('');

    const mdaRows = this.vendorsByMda
      .map(
        (m) => `
        <tr>
          <td>${escape(m.label)}</td>
          <td style="text-align:right;">${escape(m.count)}</td>
        </tr>`,
      )
      .join('');

    const feeRows = this.categoryFeeSummary
      .map(
        (r) => `
        <tr>
          <td>${escape(r.category)}</td>
          <td>${escape(r.contractRange)}</td>
          <td style="text-align:right;">${escape(this.formatCurrency(r.registrationFee))}</td>
          <td style="text-align:right;">${escape(r.vendors)}</td>
          <td style="text-align:right;">${escape(this.formatCurrency(r.revenue))}</td>
        </tr>`,
      )
      .join('');

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escape(title)}</title>
    <style>
      :root { color-scheme: light; }
      body { font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; padding: 24px; color: #111827; }
      h1 { font-size: 18px; margin: 0 0 6px; }
      .meta { font-size: 12px; color: #4B5563; margin-bottom: 16px; }
      .section { margin-top: 18px; }
      .section h2 { font-size: 14px; margin: 0 0 8px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #E5E7EB; padding: 8px; font-size: 12px; }
      th { background: #F9FAFB; text-align: left; }
      @media print { body { padding: 0; } }
    </style>
  </head>
  <body>
    <h1>${escape(title)}</h1>
    <div class="meta">
      <div><strong>Generated:</strong> ${escape(new Date().toISOString())}</div>
      <div><strong>Date range:</strong> ${escape(this.getDateRangeLabel())}</div>
      <div><strong>MDA:</strong> ${escape(this.getSelectedMdaLabel())}</div>
      <div><strong>Category:</strong> ${escape(this.getSelectedCategoryLabel())}</div>
    </div>

    <div class="section">
      <h2>Stat Cards</h2>
      <table>
        <thead><tr><th>Title</th><th style="text-align:right;">Value</th><th style="text-align:right;">Change</th><th>Subtitle</th></tr></thead>
        <tbody>${statRows}</tbody>
      </table>
    </div>

    <div class="section">
      <h2>Vendor Distribution by Category</h2>
      <table>
        <thead><tr><th>Category</th><th style="text-align:right;">Count</th></tr></thead>
        <tbody>${categoryRows || '<tr><td colspan="2">No data</td></tr>'}</tbody>
      </table>
    </div>

    <div class="section">
      <h2>Vendors by MDA</h2>
      <table>
        <thead><tr><th>MDA</th><th style="text-align:right;">Count</th></tr></thead>
        <tbody>${mdaRows || '<tr><td colspan="2">No data</td></tr>'}</tbody>
      </table>
    </div>

    <div class="section">
      <h2>Category Fee Summary</h2>
      <table>
        <thead><tr><th>Category</th><th>Contract Range</th><th style="text-align:right;">Registration Fee</th><th style="text-align:right;">Vendors</th><th style="text-align:right;">Revenue</th></tr></thead>
        <tbody>${feeRows || '<tr><td colspan="5">No data</td></tr>'}</tbody>
      </table>
    </div>
  </body>
</html>`;
  }

  private buildExportFilename(prefix: string, ext: string): string {
    const stamp = new Date().toISOString().replaceAll(':', '-');
    return `${prefix}_${stamp}.${ext}`;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 500);
  }

  private csvLine(cells: Array<string | number>): string {
    return cells
      .map((cell) => {
        const raw = String(cell ?? '');
        const escaped = raw.replaceAll('"', '""');
        return `"${escaped}"`;
      })
      .join(',');
  }

  private getSelectedMdaLabel(): string {
    if (!this.selectedMdaId) return 'All MDAs';
    return this.mdas.find((m) => m.id === this.selectedMdaId)?.name ?? this.selectedMdaId;
  }

  private getSelectedCategoryLabel(): string {
    if (!this.selectedCategoryId) return 'All Categories';
    return this.categoryOptions.find((c) => c.id === this.selectedCategoryId)?.name ?? this.selectedCategoryId;
  }

  private getDateRangeLabel(): string {
    if (this.dateRangePreset === 'all_time') return 'All time';
    if (this.dateRangePreset === 'year_to_date') return 'Year to date';
    if (this.dateRangePreset === 'last_30_days') return 'Last 30 days';
    if (this.dateRangePreset === 'last_7_days') return 'Last 7 days';
    const from = this.fromDate || 'N/A';
    const to = this.toDate || 'N/A';
    return `${from} to ${to}`;
  }

  private formatMoney(amount: number, currency: string): string {
    const safe = Number.isFinite(amount) ? amount : 0;
    const rounded = Math.round(safe);
    const prefix = currency.toUpperCase() === 'NGN' ? '₦' : `${currency.toUpperCase()} `;
    return `${prefix}${rounded.toLocaleString('en-NG')}`;
  }

  private formatCurrency(amount: number): string {
    return this.formatMoney(amount, this.analyticsCurrency || 'NGN');
  }

  private formatChange(value: number, isPercentage: boolean): string {
    if (!Number.isFinite(value) || value === 0) return '';
    const sign = value > 0 ? '+' : '';
    return isPercentage ? `${sign}${value}%` : `${sign}${value}`;
  }

  private formatDelta(value: number, includePlus = true): string {
    if (!Number.isFinite(value) || value === 0) return '';
    const sign = value > 0 && includePlus ? '+' : '';
    return `${sign}${value}`;
  }

  private shouldShowYearInDayLabels(): boolean {
    if (!this.fromDate || !this.toDate) return false;
    const fromYear = Number(String(this.fromDate).slice(0, 4));
    const toYear = Number(String(this.toDate).slice(0, 4));
    return Number.isFinite(fromYear) && Number.isFinite(toYear) && fromYear !== toYear;
  }

  private formatTrendLabel(period: string, granularity: string, showYearForDay: boolean): string {
    const raw = String(period ?? '').trim();
    if (!raw) return '';

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    if (granularity === 'month') {
      const match = /^(\d{4})-(\d{2})$/.exec(raw);
      if (!match) return raw;
      const year = match[1];
      const monthIndex = Number(match[2]) - 1;
      const monthLabel = months[monthIndex] ?? raw;
      return `${monthLabel} ${year}`;
    }

    if (granularity === 'day') {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
      if (!match) return raw;
      const year = match[1];
      const monthIndex = Number(match[2]) - 1;
      const day = match[3];
      const monthLabel = months[monthIndex] ?? raw;
      return showYearForDay ? `${day} ${monthLabel} ${year}` : `${day} ${monthLabel}`;
    }

    return raw;
  }
}
